import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { Icon } from '../components/ui/Icon.js';
import { BudgetBar } from '../components/BudgetBar.js';
import type { BudgetSummary, PriceConfidence, PriceSource } from '@meal-planner/shared';

interface PurchasableInfo {
  soldUnit: string;
  pkgLabel: string;
  packagesNeeded: number;
  totalRecipeQty: string;
  leftoverQty: string;
  isSpecialty: boolean;
}

interface ShoppingListItem {
  ingredientId: string;
  name: string;
  quantity: number;
  unit: string;
  estimatedPrice: number | null;
  priceSource: PriceSource | null;
  priceConfidence?: PriceConfidence;
  category: string;
  purchasable?: PurchasableInfo | null;
  checked?: boolean;
}

interface ShoppingListRow {
  id: string;
  mealPlanId: string;
  listData: {
    items: ShoppingListItem[];
    estimatedTotal: number | null;
    haveItIds?: string[];
  };
  createdAt: string;
}

const CATEGORY_ORDER = [
  'produce',
  'protein',
  'dairy',
  'grains',
  'baking',
  'canned',
  'condiments',
  'oils',
  'spices',
  'nuts',
  'beverage',
  'other',
];

const CATEGORY_LABEL: Record<string, string> = {
  produce: 'Produce',
  protein: 'Meat & Protein',
  dairy: 'Dairy',
  grains: 'Grains & Bakery',
  baking: 'Baking',
  canned: 'Canned & Jarred',
  condiments: 'Condiments',
  oils: 'Oils & Vinegars',
  spices: 'Spices',
  nuts: 'Nuts & Seeds',
  beverage: 'Beverages',
  other: 'Other',
};

const CATEGORY_ICON: Record<string, 'leaf' | 'flame' | 'egg' | 'cookpot' | 'plate' | 'cart'> = {
  produce: 'leaf',
  protein: 'flame',
  dairy: 'egg',
  grains: 'cookpot',
  baking: 'cookpot',
  canned: 'plate',
  condiments: 'plate',
  oils: 'plate',
  spices: 'plate',
  nuts: 'leaf',
  beverage: 'plate',
  other: 'cart',
};

function PurchasableBlock({
  item,
  isChecked,
}: {
  item: ShoppingListItem;
  isChecked: boolean;
}): JSX.Element {
  const baseStyle = {
    color: 'var(--ink-3)' as const,
    textDecoration: isChecked ? ('line-through' as const) : ('none' as const),
  };
  const p = item.purchasable;

  if (!p) {
    return (
      <div className="text-xs mt-0.5" style={baseStyle}>
        {formatQty(item.quantity)} {item.unit}
      </div>
    );
  }

  if (p.packagesNeeded === 0) {
    return (
      <div className="text-xs mt-0.5" style={{ ...baseStyle, color: 'var(--ink-4)' }}>
        ✓ Covered by pantry
      </div>
    );
  }

  return (
    <div className="text-xs mt-0.5 space-y-0.5" style={baseStyle}>
      {p.isSpecialty ? (
        <div>(see store)</div>
      ) : (
        <div>
          buy: <span style={{ color: 'var(--ink-2)' }}>{p.packagesNeeded} × {p.pkgLabel}</span>
        </div>
      )}
      {p.totalRecipeQty && (
        <div style={{ color: 'var(--ink-4)' }}>
          You'll use: {p.totalRecipeQty} this week
        </div>
      )}
      {p.leftoverQty && (
        <div style={{ color: 'var(--ink-4)' }}>
          Leftover: {p.leftoverQty} carries to pantry
        </div>
      )}
    </div>
  );
}

function sourceBadge(source: PriceSource | null): { label: string; tone: 'live' | 'estimate' } | null {
  if (source === 'kroger') return { label: 'live', tone: 'live' };
  if (source === 'usda' || source === 'bls' || source === 'manual') return { label: 'estimate', tone: 'estimate' };
  return null;
}

const FRACTIONS: Array<[number, string]> = [
  [1/8, '⅛'], [1/4, '¼'], [1/3, '⅓'], [3/8, '⅜'],
  [1/2, '½'], [5/8, '⅝'], [2/3, '⅔'], [3/4, '¾'], [7/8, '⅞'],
];
const FRAC_TOLERANCE = 0.02;

function formatQty(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return n.toString();
  const whole = Math.floor(n);
  const frac = n - whole;
  if (frac < 0.01) return whole.toString();
  for (const [val, sym] of FRACTIONS) {
    if (Math.abs(frac - val) < FRAC_TOLERANCE) {
      return whole > 0 ? `${whole} ${sym}` : sym;
    }
  }
  const s = n.toFixed(1);
  return s.endsWith('.0') ? s.slice(0, -2) : s;
}

function checkedKey(listId: string): string {
  return `pp_sl_checked_${listId}`;
}

function loadChecked(listId: string): Set<string> {
  try {
    const raw = localStorage.getItem(checkedKey(listId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

function persistChecked(listId: string, ids: Set<string>) {
  try {
    localStorage.setItem(checkedKey(listId), JSON.stringify(Array.from(ids)));
  } catch {
    /* quota or disabled storage — silent */
  }
}

export function ShoppingListPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isError } = useQuery<ShoppingListRow>({
    queryKey: ['shopping-list', id],
    queryFn: () => api.get(`/api/shopping-lists/${id}`),
    enabled: !!id,
  });

  const { data: budget } = useQuery<BudgetSummary>({
    queryKey: ['budget-summary'],
    queryFn: () => api.get('/api/me/budget-summary'),
  });

  const haveItIds = useMemo(
    () => new Set(data?.listData.haveItIds ?? []),
    [data?.listData.haveItIds],
  );

  const [checked, setChecked] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (id) setChecked(loadChecked(id));
  }, [id]);
  function toggleChecked(ingredientId: string) {
    if (!id) return;
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(ingredientId)) next.delete(ingredientId);
      else next.add(ingredientId);
      persistChecked(id, next);
      return next;
    });
  }
  function clearChecked() {
    if (!id) return;
    setChecked(new Set());
    persistChecked(id, new Set());
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-3 p-6">
          <div className="h-12 rounded-lg w-1/2" style={{ background: 'var(--surface-2)' }} />
          <div className="h-24 rounded-lg" style={{ background: 'var(--surface-2)' }} />
          <div className="h-64 rounded-lg" style={{ background: 'var(--surface-2)' }} />
        </div>
      </Layout>
    );
  }

  if (isError || !data) {
    return (
      <Layout>
        <p className="p-6 text-sm" style={{ color: 'var(--danger, #e11d48)' }}>Shopping list not found.</p>
      </Layout>
    );
  }

  return (
    <ShopStage
      data={data}
      haveItIds={haveItIds}
      budget={budget}
      checked={checked}
      onCheckToggle={toggleChecked}
      onClearChecked={clearChecked}
      mealPlanId={data.mealPlanId}
    />
  );
}

function ShopStage({
  data,
  haveItIds,
  budget,
  checked,
  onCheckToggle,
  onClearChecked,
  mealPlanId,
}: {
  data: ShoppingListRow;
  haveItIds: Set<string>;
  budget: BudgetSummary | undefined;
  checked: Set<string>;
  onCheckToggle: (id: string) => void;
  onClearChecked: () => void;
  mealPlanId: string;
}) {
  const navigate = useNavigate();

  const filteredItems = useMemo(
    () => data.listData.items.filter((i) => !haveItIds.has(i.ingredientId)),
    [data.listData.items, haveItIds],
  );

  const { grouped, unpriced } = useMemo(() => {
    const buckets = new Map<string, ShoppingListItem[]>();
    const tbd: ShoppingListItem[] = [];
    for (const item of filteredItems) {
      if (item.estimatedPrice == null) {
        tbd.push(item);
        continue;
      }
      const cat = CATEGORY_LABEL[item.category] ? item.category : 'other';
      if (!buckets.has(cat)) buckets.set(cat, []);
      buckets.get(cat)!.push(item);
    }
    const groupedOut = CATEGORY_ORDER
      .filter((c) => buckets.has(c))
      .map((c) => ({ category: c, items: buckets.get(c)!.sort((a, b) => a.name.localeCompare(b.name)) }));
    return { grouped: groupedOut, unpriced: tbd.sort((a, b) => a.name.localeCompare(b.name)) };
  }, [filteredItems]);

  const total = filteredItems.length;
  const checkedCount = filteredItems.filter((i) => checked.has(i.ingredientId)).length;
  const progressPct = total === 0 ? 0 : Math.round((checkedCount / total) * 100);

  const estimatedTotal = useMemo(() => {
    let sum = 0;
    let any = false;
    for (const item of filteredItems) {
      if (item.estimatedPrice == null) continue;
      any = true;
      sum += item.estimatedPrice;
    }
    return any ? sum : null;
  }, [filteredItems]);

  const dollarsLeft = useMemo(() => {
    let sum = 0;
    let any = false;
    for (const item of filteredItems) {
      if (item.estimatedPrice == null) continue;
      any = true;
      if (!checked.has(item.ingredientId)) sum += item.estimatedPrice;
    }
    return any ? sum : null;
  }, [filteredItems, checked]);

  return (
    <Layout>
      <div className="p-6">
        <button
          onClick={() => navigate(`/ingredient-checklist/${mealPlanId}`)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase mb-4 hover:underline"
          style={{ color: 'var(--ink-3)' }}
        >
          <Icon name="arrow" size={12} style={{ transform: 'rotate(180deg)' }} />
          Back to checklist
        </button>

        <div className="mb-6">
          <div
            className="text-xs font-semibold tracking-[0.16em] uppercase mb-2"
            style={{ color: 'var(--accent)' }}
          >
            Grocery run
          </div>
          <h1 className="h-display" style={{ margin: 0, fontSize: 44, lineHeight: 1.05, color: 'var(--ink)' }}>
            Shopping <span className="h-serif" style={{ fontStyle: 'italic', color: 'var(--accent)' }}>list</span>
          </h1>
        </div>

        {total > 0 && (
          <div className="card p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <div className="text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: 'var(--ink-3)' }}>
                Progress
              </div>
              <div className="h-display" style={{ fontSize: 32, lineHeight: 1, color: 'var(--ink)' }}>
                {checkedCount}<span style={{ color: 'var(--ink-4)', fontSize: 22 }}> / {total}</span>
              </div>
              <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${progressPct}%`, background: 'var(--accent)' }}
                />
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: 'var(--ink-3)' }}>
                Estimated remaining
              </div>
              <div className="h-display" style={{ fontSize: 32, lineHeight: 1, color: 'var(--ink)' }}>
                {dollarsLeft == null ? <span style={{ color: 'var(--ink-4)' }}>—</span> : `$${dollarsLeft.toFixed(2)}`}
              </div>
              <div className="text-xs mt-3" style={{ color: 'var(--ink-3)' }}>
                {estimatedTotal != null
                  ? <>of <span style={{ color: 'var(--ink-2)' }}>${estimatedTotal.toFixed(2)}</span> total</>
                  : 'Prices coming soon'}
              </div>
            </div>

            <div className="flex flex-col items-start md:items-end justify-between">
              <div className="text-xs font-semibold tracking-wider uppercase mb-2" style={{ color: 'var(--ink-3)' }}>
                {progressPct === 100 ? 'All set' : 'Status'}
              </div>
              <div className="flex items-center gap-2">
                {progressPct === 100 ? (
                  <>
                    <Icon name="check" size={16} style={{ color: 'var(--accent)' }} />
                    <span className="text-sm font-medium" style={{ color: 'var(--accent)' }}>
                      Cart complete
                    </span>
                  </>
                ) : (
                  <span className="text-sm" style={{ color: 'var(--ink-2)' }}>
                    {total - checkedCount} left to grab
                  </span>
                )}
              </div>
              {checkedCount > 0 && (
                <button
                  onClick={onClearChecked}
                  className="mt-3 text-xs font-medium hover:underline"
                  style={{ color: 'var(--ink-3)' }}
                >
                  Reset check-offs
                </button>
              )}
            </div>
          </div>
        )}

      {filteredItems.length > 0 && (
        <BudgetBar
          weeklyBudget={budget?.weeklyBudget ?? null}
          currentCost={budget?.truePurchaseCost ?? estimatedTotal}
          recipeCost={budget?.currentPlanCost ?? estimatedTotal}
          leftoverValue={budget?.leftoverValue ?? null}
          estimatedMinimumWeeklyBudget={budget?.estimatedMinimumWeeklyBudget ?? null}
          truePurchaseStatus={budget?.truePurchaseStatus}
          pricedCount={filteredItems.length - unpriced.length}
          unpricedCount={unpriced.length}
        />
      )}

        {estimatedTotal != null && (
          <div className="mb-4 text-sm" style={{ color: 'var(--ink-2)' }}>
            Estimated total:{' '}
            <span className="font-medium" style={{ color: 'var(--ink)' }}>
              ${estimatedTotal.toFixed(2)}
            </span>
          </div>
        )}

        {grouped.length === 0 && unpriced.length === 0 ? (
          <div className="card px-6 py-10 text-center">
            <Icon name="check" size={32} style={{ color: 'var(--accent)', margin: '0 auto' }} />
            <p className="mt-3 text-sm" style={{ color: 'var(--ink-2)' }}>
              You've got everything for this plan — no shopping needed.
            </p>
            <button
              onClick={() => navigate(`/ingredient-checklist/${mealPlanId}`)}
              className="btn btn-ghost mt-4"
              style={{ padding: '10px 18px', fontSize: 13 }}
            >
              Back to checklist
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map(({ category, items }) => {
              const sectionChecked = items.filter((i) => checked.has(i.ingredientId)).length;
              const sectionTotal = items.length;
              return (
                <section key={category}>
                  <header className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <Icon name={CATEGORY_ICON[category] ?? 'cart'} size={14} style={{ color: 'var(--accent)' }} />
                      <h2 className="text-xs font-semibold tracking-[0.16em] uppercase" style={{ color: 'var(--ink-2)' }}>
                        {CATEGORY_LABEL[category] ?? category}
                      </h2>
                    </div>
                    <span className="text-[11px] font-mono" style={{ color: 'var(--ink-3)' }}>
                      {sectionChecked} / {sectionTotal}
                    </span>
                  </header>

                  <ul className="card divide-y" style={{ borderColor: 'var(--line-soft)' }}>
                    {items.map((item) => {
                      const isChecked = checked.has(item.ingredientId);
                      return (
                        <li key={item.ingredientId} style={{ borderColor: 'var(--line-soft)' }}>
                          <button
                            onClick={() => onCheckToggle(item.ingredientId)}
                            className="w-full flex items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-[var(--surface-2)]"
                            aria-pressed={isChecked}
                          >
                            <span
                              className="flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0 transition-all"
                              style={{
                                background: isChecked ? 'var(--accent)' : 'transparent',
                                border: `1.5px solid ${isChecked ? 'var(--accent)' : 'var(--line-soft)'}`,
                              }}
                            >
                              {isChecked && (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent-ink)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M5 12.5L10 17l9-10" />
                                </svg>
                              )}
                            </span>

                            <div className="flex-1 min-w-0">
                              <div
                                className="text-sm font-medium truncate"
                                style={{
                                  color: isChecked ? 'var(--ink-4)' : 'var(--ink)',
                                  textDecoration: isChecked ? 'line-through' : 'none',
                                }}
                              >
                                {item.name}
                              </div>
                              <div
                                className="text-xs mt-0.5"
                                style={{
                                  color: 'var(--ink-3)',
                                  textDecoration: isChecked ? 'line-through' : 'none',
                                }}
                              >
                                {item.quantity} {item.unit}
                              </div>
                            </div>

                            <div
                              className="text-right flex-shrink-0"
                              style={{
                                color: isChecked ? 'var(--ink-4)' : 'var(--ink-2)',
                                textDecoration: isChecked ? 'line-through' : 'none',
                              }}
                            >
                              {item.estimatedPrice != null ? (
                                <>
                                  <div className="text-sm font-mono">
                                    ${item.estimatedPrice.toFixed(2)}
                                  </div>
                                  {(() => {
                                    const badge = sourceBadge(item.priceSource);
                                    if (!badge) return null;
                                    const isLive = badge.tone === 'live';
                                    return (
                                      <span
                                        className="inline-block mt-0.5 px-1.5 py-px rounded-full text-[9px] uppercase tracking-wider font-semibold"
                                        style={{
                                          background: isLive ? 'var(--accent-soft)' : 'var(--surface-2)',
                                          color: isLive ? 'var(--accent)' : 'var(--ink-3)',
                                        }}
                                      >
                                        {badge.label}
                                      </span>
                                    );
                                  })()}
                                </>
                              ) : (
                                <span className="text-[11px]" style={{ color: 'var(--ink-4)' }}>
                                  TBD
                                </span>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}

            {unpriced.length > 0 && (
              <details className="card px-5 py-3">
                <summary
                  className="cursor-pointer text-xs font-semibold tracking-[0.16em] uppercase"
                  style={{ color: 'var(--ink-3)' }}
                >
                  Price TBD ({unpriced.length} {unpriced.length === 1 ? 'item' : 'items'})
                </summary>
                <ul className="mt-3 divide-y" style={{ borderColor: 'var(--line-soft)' }}>
                  {unpriced.map((item) => {
                    const isChecked = checked.has(item.ingredientId);
                    return (
                      <li key={item.ingredientId} style={{ borderColor: 'var(--line-soft)' }}>
                        <button
                          onClick={() => onCheckToggle(item.ingredientId)}
                          className="w-full flex items-center gap-4 py-2 text-left transition-colors hover:bg-[var(--surface-2)]"
                          aria-pressed={isChecked}
                        >
                          <span
                            className="flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0 transition-all"
                            style={{
                              background: isChecked ? 'var(--accent)' : 'transparent',
                              border: `1.5px solid ${isChecked ? 'var(--accent)' : 'var(--line-soft)'}`,
                            }}
                          >
                            {isChecked && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent-ink)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12.5L10 17l9-10" />
                              </svg>
                            )}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div
                              className="text-sm font-medium truncate"
                              style={{
                                color: isChecked ? 'var(--ink-4)' : 'var(--ink)',
                                textDecoration: isChecked ? 'line-through' : 'none',
                              }}
                            >
                              {item.name}
                            </div>
                            <PurchasableBlock item={item} isChecked={isChecked} />
                          </div>
                          <span className="text-[11px]" style={{ color: 'var(--ink-4)' }}>TBD</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
